import { useRef } from 'react'
import { cn } from '@/lib/utils'

// The colour picker shown inside the floating style toolbar and settings dialogs.
// TradingView-matched: greyscale ramp + 10 hues × (bold base + 5 shades), plus an
// opacity bar with a % box.

/** hex → rgba string so opacity can be applied to the chosen colour. */
export function rgba(hex: string, pct: number): string {
  const n = parseInt(hex.slice(1), 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${pct / 100})`
}

const GREYS = ['#ffffff', '#d9d9d9', '#bdbdbd', '#9e9e9e', '#7c7c7c', '#5f5f5f', '#454545', '#2e2e2e', '#171717', '#000000']
const HUE_BASES = ['#f23645', '#ff9800', '#ffd60a', '#4caf50', '#26a69a', '#22d3ee', '#2970ff', '#5c6bc0', '#ab47bc', '#ec407a']

function mix(hex: string, target: string, t: number): string {
  const a = parseInt(hex.slice(1), 16)
  const b = parseInt(target.slice(1), 16)
  const ch = (sh: number) => {
    const x = (a >> sh) & 255
    const y = (b >> sh) & 255
    return Math.round(x + (y - x) * t)
  }
  return '#' + [ch(16), ch(8), ch(0)].map((n) => n.toString(16).padStart(2, '0')).join('')
}

// Bold base row sits on top (easy-pick); below it, after a gap, 5 shade rows
// (lighter pastels → darker), matching TV's layout.
const SHADE_FNS: Array<(h: string) => string> = [
  (h) => mix(h, '#ffffff', 0.7),
  (h) => mix(h, '#ffffff', 0.45),
  (h) => mix(h, '#ffffff', 0.2),
  (h) => mix(h, '#000000', 0.28),
  (h) => mix(h, '#000000', 0.52),
]
const SHADE_ROWS = SHADE_FNS.map((fn) => HUE_BASES.map(fn))

/**
 * Hues a TRADE IDEA may not wear, whatever the trader picks.
 *
 * An idea's colour is an IDENTITY, and no identity may collide with a colour
 * that carries MEANING elsewhere on this chart. Red is the sell/loss side and
 * green the buy/gain side — an idea in either paints its entry, its stop AND
 * its target in a colour that already says something else about them. The teal
 * goes with the green because `#26a69a` IS the app's success token, the gain
 * colour spelled exactly rather than merely something near it.
 *
 * YELLOW STAYS. It is `DEFAULT_DRAW_COLOR`, so the auto rotation must never
 * hand it out (see `IDEA_COLORS` in `tradeIdeaVocab.ts`) — but a colour nobody
 * should be GIVEN is not a colour nobody may CHOOSE. A trader who reaches for
 * yellow deliberately has accepted the resemblance to a trend line; the three
 * hues above are barred because no deliberate choice can make an idea painted
 * in the gain or loss colour readable.
 *
 * THE SHARED LIST IS NOT TOUCHED. A red trend line means nothing in
 * particular, so drawings keep all ten hues; the idea's set is filtered out
 * here rather than cut from `HUE_BASES`, which would quietly narrow every
 * drawing in the app to fix a trade-idea problem.
 */
const TRADE_IDEA_EXCLUDED = ['#f23645', '#4caf50', '#26a69a']
const TRADE_IDEA_HUES = HUE_BASES.filter((h) => !TRADE_IDEA_EXCLUDED.includes(h))
const TRADE_IDEA_SHADE_ROWS = SHADE_FNS.map((fn) => TRADE_IDEA_HUES.map(fn))

interface SwatchGridProps {
  cur?: string | null
  onPick: (color: string) => void
  /**
   * `tradeIdea` drops the banned hues and their WHOLE shade columns — a
   * 70%-white tint of the sell red is still unmistakably red, so filtering the
   * bold row alone would leave five paler ways to break the same rule.
   */
  scope?: 'tradeIdea'
}

/**
 * Just the swatch grid (greys + bold hues + shades), no opacity. Reused for
 * per-fib-level colours where opacity isn't wanted.
 */
export function SwatchGrid({ cur, onPick, scope }: SwatchGridProps) {
  const ti = scope === 'tradeIdea'
  const hues = ti ? TRADE_IDEA_HUES : HUE_BASES
  const rows = ti ? TRADE_IDEA_SHADE_ROWS : SHADE_ROWS
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        <div className="flex gap-1">
          {GREYS.map((c) => (
            <Swatch key={c} c={c} cur={cur} onPick={() => onPick(c)} />
          ))}
        </div>
        <div className="flex gap-1">
          {hues.map((c) => (
            <Swatch key={c} c={c} cur={cur} onPick={() => onPick(c)} />
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-1">
        {rows.map((row, i) => (
          <div key={i} className="flex gap-1">
            {row.map((c) => (
              <Swatch key={c} c={c} cur={cur} onPick={() => onPick(c)} />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

interface ColorPopoverProps {
  curColor: string
  curOpacity: number
  onColor: (color: string) => void
  onOpacity: (opacity: number) => void
}

export function ColorPopover({ curColor, curOpacity, onColor, onOpacity }: ColorPopoverProps) {
  return (
    <div className="flex flex-col gap-2">
      <SwatchGrid cur={curColor} onPick={onColor} />
      <div className="flex flex-col gap-1.5 pt-0.5">
        <span className="text-caption text-muted-foreground">Opacity</span>
        <OpacityRow color={curColor} value={curOpacity} onChange={onOpacity} />
      </div>
    </div>
  )
}

function Swatch({ c, cur, onPick }: { c: string; cur?: string | null; onPick: () => void }) {
  const active = cur?.toLowerCase() === c.toLowerCase()
  return (
    <button
      type="button"
      onClick={onPick}
      aria-label={c}
      style={{ backgroundColor: c }}
      className={cn(
        'h-5 w-5 rounded-sm cursor-pointer',
        active ? 'ring-2 ring-primary ring-offset-1 ring-offset-popover' : 'border border-border/50',
      )}
    />
  )
}

// Opacity bar: checkerboard base + transparent→colour gradient + white thumb,
// plus a % box that reflects (and drives) the bar. Matches TV.
function OpacityRow({
  color,
  value,
  onChange,
}: {
  color: string
  value: number
  onChange: (value: number) => void
}) {
  const trackRef = useRef<HTMLDivElement | null>(null)
  const setFromX = (clientX: number) => {
    const el = trackRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    onChange(Math.round(Math.max(0, Math.min(1, (clientX - r.left) / r.width)) * 100))
  }
  return (
    <div className="flex items-center gap-2">
      <div
        ref={trackRef}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId)
          setFromX(e.clientX)
        }}
        onPointerMove={(e) => {
          if (e.buttons & 1) setFromX(e.clientX)
        }}
        className="relative h-2.5 flex-1 rounded-full cursor-pointer touch-none"
        style={{
          backgroundImage: `linear-gradient(to right, rgba(0,0,0,0), ${color}), conic-gradient(#4a4a4a 25%, #6a6a6a 0 50%, #4a4a4a 0 75%, #6a6a6a 0)`,
          backgroundSize: '100% 100%, 8px 8px',
        }}
      >
        <div
          className="absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-border bg-white shadow-sm pointer-events-none"
          style={{ left: `${value}%` }}
        />
      </div>
      <div className="flex items-center h-7 w-14 rounded-md border border-border px-1.5">
        <input
          type="number"
          min={0}
          max={100}
          value={value}
          onChange={(e) => onChange(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
          className="w-full bg-transparent text-caption text-foreground text-right outline-none tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
        />
        <span className="pl-0.5 text-caption text-muted-foreground">%</span>
      </div>
    </div>
  )
}
